import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { NavCityDropdown } from "./NavCityDropdown";
import {
	FINCARAIZ_DROPDOWN_BY_HEADER_LINK_ID,
	FINCARAIZ_EXCLUDED_HEADER_LINK_IDS,
	buildFincaraizDropdownPanel,
} from "./fincaraizNav.config";
import type { NavDropdownKey } from "./fincaraizNav.config";

type FincaraizHeaderMenuProps = {
	type: "desktop" | "mobile";
};

const renderNavLink = (
	href: string,
	title: string,
	linkClass: string,
	countryCode: string,
	key: string
) => (
	<a
		className={`${linkClass} ${countryCode}`}
		href={href}
		key={key}
		target={href.startsWith("http") ? "_blank" : undefined}
		rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
	>
		{title}
	</a>
);

export const FincaraizHeaderMenu = ({ type = "desktop" }: FincaraizHeaderMenuProps) => {
	const { header_links, country_code } = useContext(ConfigStateContext);
	const [openDropdownKey, setOpenDropdownKey] = useState<NavDropdownKey | null>(null);
	const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const navClass = type === "desktop" ? "header_desktop_menu" : "header_mobile_menu";
	const linkClass = type === "desktop" ? "header_btn" : "header_btn_mobile";
	const isMobile = type === "mobile";

	const visibleLinks = useMemo(() => {
		const links = header_links ?? [];
		return links.filter(({ id }) => !FINCARAIZ_EXCLUDED_HEADER_LINK_IDS.includes(id));
	}, [header_links]);

	const handleToggle = (key: NavDropdownKey) => {
		setOpenDropdownKey(prev => (prev === key ? null : key));
	};

	const handleClose = () => setOpenDropdownKey(null);

	const clearHoverCloseTimeout = () => {
		if (hoverCloseTimeoutRef.current) {
			clearTimeout(hoverCloseTimeoutRef.current);
			hoverCloseTimeoutRef.current = null;
		}
	};

	const handleDropdownMouseEnter = (key: NavDropdownKey) => {
		clearHoverCloseTimeout();
		setOpenDropdownKey(key);
	};

	const handleDropdownMouseLeave = () => {
		clearHoverCloseTimeout();
		hoverCloseTimeoutRef.current = setTimeout(() => setOpenDropdownKey(null), 120);
	};

	useEffect(() => () => clearHoverCloseTimeout(), []);

	return (
		<nav className={navClass}>
			{visibleLinks.map(({ link, title, id }) => {
				const dropdownKey = FINCARAIZ_DROPDOWN_BY_HEADER_LINK_ID[id];
				const navKey = `fincaraiz-nav-${id}`;

				if (dropdownKey) {
					const panel = buildFincaraizDropdownPanel(dropdownKey, link);

					return (
						<NavCityDropdown
							key={navKey}
							label={title}
							cities={panel.cities}
							seeAllHref={panel.seeAllHref}
							variant={panel.variant}
							isMobile={isMobile}
							linkClass={linkClass}
							countryCode={country_code}
							isOpen={openDropdownKey === dropdownKey}
							onToggle={() => handleToggle(dropdownKey)}
							onClose={handleClose}
							onMouseEnter={!isMobile ? () => handleDropdownMouseEnter(dropdownKey) : undefined}
							onMouseLeave={!isMobile ? handleDropdownMouseLeave : undefined}
						/>
					);
				}

				return renderNavLink(link, title, linkClass, country_code, navKey);
			})}
		</nav>
	);
};
